import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router';
import config from '../../config';
import DatePicker from 'react-datepicker';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import StockPaymentModel from '../StockPayment/StockPaymentModel';
import StockVisePayment from '../StockPayment/StockVisePayment';

function SupplierAllDues() {
    const { supplierId } = useParams();
    const location = useLocation();
    const { supplierName } = location.state || { supplierName: 'Unknown Supplier' };

    const [totalChequeAmount, setTotalChequeAmount] = useState(0);
    const [dueAmount, setDueAmount] = useState(0);
    const [dueCount, setDueCount] = useState(0);
    const [error, setError] = useState(null);
    const [stock, setStock] = useState([]);
    const [groups, setGroups] = useState([]);
    const [cheques, setCheques] = useState({});
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedStockPayments, setSelectedStockPayments] = useState([]);

    const [showPaidInvoices, setShowPaidInvoices] = useState(true);
    const [showUnpaidInvoices, setShowUnpaidInvoices] = useState(true);
    const [showPayments, setShowPayments] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5)

    useEffect(() => {
        if (supplierId) {
            fetchDueAmount(supplierId);
            fetchChequeAmount(supplierId);
            fetchDueAmountCount(supplierId);
            fetchStocks(supplierId);
        }
    }, [supplierId]);

    const fetchChequeAmount = async (supplierId) => {
        try {
            const response = await fetch(`${config.BASE_URL}/cheques/pendingTotal/${supplierId}`);
            const data = await response.json();
            setTotalChequeAmount(data.totalAmount);
        } catch (error) {
            console.error('Error fetching dated cheques amount:', error);
            return 0;
        }
    };

    const fetchDueAmount = async (supplierId) => {
        try {
            const response = await fetch(`${config.BASE_URL}/stockPayments/totalDues/${supplierId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch unpaid invoice amount');
            }
            const result = await response.json();
            setDueAmount(result.totalDue);
        } catch (error) {
            setError(error.message);
        }
    };

    const fetchDueAmountCount = async (supplierId) => {
        try {
            const response = await fetch(`${config.BASE_URL}/stockPayments/duePayments/${supplierId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch unpaid invoices');
            }
            const result = await response.json();
            setDueCount(result.unpaidInvoices);
        } catch (error) {
            setError(error.message);
        }
    };

    const fetchStocks = async (supplierId) => {
        try {
            const response = await fetch(`${config.BASE_URL}/stock/supplier/${supplierId}`);
            if (!response.ok) throw new Error('Failed to fetch stock');
            const result = await response.json();
            setStock(result);
        } catch (error) {
            setError(error.message);
        }
    };

    useEffect(() => {
        if (stock.length > 0) {
            const grouped = stock.reduce((acc, item) => {
                const dateObj = new Date(item.stockDate);
                const date = `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;
                const stockName = item.stockName || 'N/A';

                const key = `${date}_${stockName}`;

                if (!acc[key]) {
                    acc[key] = {
                        date,
                        stockName,
                        items: [],
                        total: 0,
                        status: item?.stockPayment?.stockPaymentStatus || 'Unpaid',
                        due: item?.stockPayment?.due || 0,
                        stockPaymentId: item?.stockPayment?.stockPaymentId,
                        dateObj
                    };
                }

                acc[key].items.push(item);
                acc[key].total += item.stockPrice;
                return acc;
            }, {});

            const groupArray = Object.values(grouped)
                .sort((a, b) => b.dateObj - a.dateObj);

            setGroups(groupArray);

            groupArray.forEach(group => {
                if (group.stockPaymentId) {
                    fetchCheques(group.stockPaymentId);
                }
            });
        } else {
            setGroups([]);
        }
    }, [stock]);


    const fetchCheques = async (stockPaymentId) => {
        try {
            const response = await fetch(`${config.BASE_URL}/cheques/stockPayment/${stockPaymentId}`);
            if (!response.ok) throw new Error("Failed to fetch cheques");
            const data = await response.json();
            setCheques(prevCheques => ({
                ...prevCheques,
                [stockPaymentId]: data
            }));
        } catch (error) {
            setError(error.message);
        }
    };

    const handlePaidInvoicesChange = (e) => {
        setShowPaidInvoices(e.target.checked);
    };

    const handleUnpaidInvoicesChange = (e) => {
        setShowUnpaidInvoices(e.target.checked);
    };

    const handlePaymentsChange = (e) => {
        setShowPayments(e.target.checked);
    };

    const filteredGroups = groups.filter(group => {
        if (!(group.status === 'Paid' && showPaidInvoices) &&
            !(group.status === 'Unpaid' && showUnpaidInvoices)) {
            return false;
        }

        if (startDate && endDate) {
            const groupDate = group.dateObj;
            return groupDate >= startDate && groupDate <= endDate;
        }

        return true;
    });

    useEffect(() => {
        const newTotalPages = Math.ceil(filteredGroups.length / itemsPerPage);
        if (newTotalPages >= 10) {
            setCurrentPage(newTotalPages);
        }
    }, [filteredGroups, itemsPerPage]);

    const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    const displayPages = totalPages >= 10 ? [...pageNumbers].reverse() : pageNumbers;


    const actualPage = totalPages >= 10
        ? totalPages - currentPage + 1
        : currentPage;

    const indexOfLastItem = actualPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentGroups = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);

    const handlePrevious = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };

    const generatePDF = () => {
        const doc = new jsPDF();

        doc.setFontSize(18);
        doc.text(`${supplierName} Transaction History`, 14, 20);

        doc.setFontSize(12);
        doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 30);
        doc.text(`Supplier Name: ${supplierName}`, 14, 38);
        doc.text(`Unpaid Invoices: ${dueCount}`, 14, 46);
        doc.text(`Total Credit: LKR ${Number(dueAmount).toLocaleString('en-US')}`, 14, 54);
        doc.text(`Dated Cheques: LKR ${Number(totalChequeAmount).toLocaleString('en-US')}`, 14, 62);
        doc.text(`Total Payable: LKR ${(Number(totalChequeAmount) + Number(dueAmount)).toLocaleString('en-US')}`, 14, 70);

        if (filteredGroups.length > 0) {
            let yPos = 90;

            doc.setFontSize(14);
            doc.text("Purchase Details", 14, yPos);
            yPos += 10;

            filteredGroups.forEach((group, index) => {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text(`Date: ${group.date}`, 14, yPos);
                yPos += 8;

                const tableData = group.items.map(item => [
                    item?.product?.productName || 'N/A',
                    item.stockQty,
                    `LKR ${Number(item.unitPrice).toLocaleString('en-US')}`,
                    `LKR ${Number(item.stockPrice).toLocaleString('en-US')}`
                ]);

                doc.autoTable({
                    startY: yPos,
                    head: [['Product', 'QTY', 'Unit Rate', 'Total']],
                    body: tableData,
                    theme: 'striped',
                    headStyles: { fillColor: [66, 139, 202] },
                    margin: { left: 14 }
                });

                yPos = doc.lastAutoTable.finalY + 5;

                doc.setFont(undefined, 'normal');
                doc.text(`Total: LKR ${Number(group.total).toLocaleString('en-US')}`, 14, yPos);
                yPos += 6;
                doc.text(`Balance: LKR ${Number(group.due).toLocaleString('en-US')}`, 14, yPos);
                yPos += 6;
                doc.text(`Status: ${group.status}`, 14, yPos);
                yPos += 10;

                if (showPayments && cheques[group.stockPaymentId]?.length > 0) {
                    if (yPos > 250) {
                        doc.addPage();
                        yPos = 20;
                    }

                    doc.setFont(undefined, 'bold');
                    doc.text("Cheque Details:", 14, yPos);
                    yPos += 8;

                    const chequeData = cheques[group.stockPaymentId].map(cheque => [
                        cheque.chequeNumber,
                        `LKR ${Number(cheque.chequeAmount).toLocaleString('en-US')}`,
                        new Date(cheque.issuedDate).toLocaleDateString('en-GB'),
                        cheque.chequeStatus
                    ]);

                    doc.autoTable({
                        startY: yPos,
                        head: [['Cheque Number', 'Amount', 'Issue Date', 'Status']],
                        body: chequeData,
                        theme: 'grid',
                        headStyles: { fillColor: [147, 112, 219] },
                        margin: { left: 14 }
                    });

                    yPos = doc.lastAutoTable.finalY + 15;
                } else {
                    yPos += 10;
                }

                if (index < filteredGroups.length - 1) {
                    doc.setDrawColor(200, 200, 200);
                    doc.line(14, yPos - 5, 196, yPos - 5);
                    yPos += 5;
                }
            });
        } else {
            doc.text("No matching transactions found", 14, 100);
        }

        doc.save(`${supplierName}_Transaction_History.pdf`);
    };

    return (
        <div>
            <div className="scrolling-container">
                <div className="mt-4">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: "20px" }}>
                        <div style={{ paddingLeft: "10px", width: "100%" }}>

                            <div style={{ marginBottom: '2rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1a1a1a', margin: 0 }}> {supplierName || 'Unknown Supplier'} Transaction History</h3>
                            </div>

                            <div className='row' style={{ display: 'grid', gridTemplateColumns: 'auto 1px 1fr', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                                <div className='col-md-6'>
                                    <table className="table table-hover table-striped mb-0" style={{ width: '100px' }}>

                                        <tbody className="bg-light">
                                            <tr>
                                                <td className="text-start ps-4 fw-bold">Supplier Name</td>
                                                <td className="text-end pe-4 font-monospace">{supplierName}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-start ps-4 fw-bold">No. of unpaid invoices</td>
                                                <td className="text-end pe-4 font-monospace">{dueCount}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-start ps-4 fw-bold">Total Credit</td>
                                                <td className="text-end pe-4 font-monospace">
                                                    LKR {Number(dueAmount).toLocaleString('en-US')}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-start ps-4 fw-bold">Dated Cheques</td>
                                                <td className="text-end pe-4 font-monospace">
                                                    LKR {Number(totalChequeAmount).toLocaleString('en-US')}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-start ps-4 fw-bold">Total Payable</td>
                                                <td className="text-end pe-4 font-monospace fw-bold text-success">
                                                    LKR {(Number(totalChequeAmount) + Number(dueAmount)).toLocaleString('en-US')}
                                                </td>
                                            </tr>

                                        </tbody>
                                    </table>
                                </div>

                                <div className='col-md-2'>
                                    <div style={{ backgroundColor: 'white', height: '100%', width: '1px' }}></div>
                                </div>

                                <div className='col-md-6' style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingLeft: '1rem' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>

                                        <h4 className='text-center' style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#444', width: '100%', justifyContent: 'start' }}>
                                            Show Transactions
                                        </h4>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#444', cursor: 'pointer', justifyContent: 'start' }}>
                                            <input
                                                type="checkbox"
                                                style={{ width: '14px', height: '14px', accentColor: '#007bff' }}
                                                checked={showPaidInvoices}
                                                onChange={handlePaidInvoicesChange}
                                            />
                                            <span style={{ fontSize: '0.8rem' }}>Paid Invoices</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#444', cursor: 'pointer', justifyContent: 'start' }}>
                                            <input
                                                type="checkbox"
                                                style={{ width: '14px', height: '14px', accentColor: '#007bff' }}
                                                checked={showUnpaidInvoices}
                                                onChange={handleUnpaidInvoicesChange}
                                            />
                                            <span style={{ fontSize: '0.8rem' }}>Unpaid Invoices</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#444', cursor: 'pointer', justifyContent: 'start' }}>
                                            <input
                                                type="checkbox"
                                                style={{ width: '14px', height: '14px', accentColor: '#007bff' }}
                                                checked={showPayments}
                                                onChange={handlePaymentsChange}
                                            />
                                            <span style={{ fontSize: '0.8rem' }}>Payments</span>
                                        </label>

                                        <div className='row'>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#444', cursor: 'pointer', justifyContent: 'start', marginBottom: '10px' }}>
                                                    Filter by Date Range
                                                </label>
                                                <DatePicker
                                                    selectsRange
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    onChange={(update) => {
                                                        setStartDate(update[0]);
                                                        setEndDate(update[1]);
                                                    }}
                                                    placeholderText="Select Date Range"
                                                    className="form-control"
                                                    dateFormat="yyyy-MM-dd"
                                                    style={{ width: '250px', height: '40px', fontSize: "14px", padding: "8px 12px" }}
                                                    isClearable
                                                />
                                            </div>

                                            <div className='row mt-3' style={{ display: 'flex', gap: '10px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <button
                                                        onClick={generatePDF}
                                                        style={{ width: '100%', padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', boxSizing: 'border-box', height: '40px' }}>
                                                        Print PDF
                                                    </button>
                                                </div>

                                                {selectedPayments.length > 0 && (
                                                    <div style={{ flex: 1 }}>
                                                        <button
                                                            onClick={() => {
                                                                const selectedGroups = groups.filter(group =>
                                                                    selectedPayments.includes(group.stockPaymentId)
                                                                );
                                                                setSelectedStockPayments(selectedGroups);
                                                                setShowPaymentModal(true);
                                                            }}
                                                            style={{ width: '100%', padding: '10px 20px', backgroundColor: '#28A745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', boxSizing: 'border-box', height: '40px' }}>
                                                            Make A payment
                                                        </button>
                                                    </div>
                                                )}

                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem' }}>
                                    Purchase details
                                </h5>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Date of Purchase</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Stock Name/Batch No</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Product</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>QTY</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Unit Rate</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Total</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}></th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Balance (Rs)</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Paid / Unpaid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentGroups.length > 0 ? (
                                            currentGroups.map((group, groupIndex) => (
                                                <React.Fragment key={groupIndex}>
                                                    {group.items.map((item, itemIndex) => (
                                                        <tr key={`${groupIndex}-${itemIndex}`}
                                                            style={{ backgroundColor: itemIndex % 2 === 0 ? '#fff' : '#f8f8f8' }}>
                                                            <td style={{ padding: '12px' }}>
                                                                {itemIndex === 0 ? group.date : ''}
                                                            </td>
                                                            <td style={{ padding: '12px' }}>
                                                                {itemIndex === 0 ? group.stockName : ''}
                                                            </td>
                                                            <td style={{ padding: '12px', textAlign: 'left' }}>{item?.product?.productName || 'N/A'}</td>
                                                            <td style={{ padding: '12px', textAlign: 'left' }}>{item.stockQty}</td>
                                                            <td style={{ padding: '12px', textAlign: 'left' }}> LKR {Number(item.unitPrice).toLocaleString('en-US')} </td>
                                                            <td style={{ padding: '12px', textAlign: 'left' }}> LKR {Number(item.stockPrice).toLocaleString('en-US')} </td>
                                                            <td style={{ padding: '12px', textAlign: 'left' }}></td>
                                                        </tr>
                                                    ))}
                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                        <td colSpan="3"></td>
                                                        <td style={{ padding: '12px', textAlign: 'left' }}></td>
                                                        <td style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>TOTAL</td>
                                                        <td style={{ padding: '12px', textAlign: 'left' }}></td>
                                                        <td style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}> LKR {Number(group.total).toLocaleString('en-US')} </td>
                                                        <td style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}> LKR {Number(group.due).toLocaleString('en-US')}</td>
                                                        <td style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: group.status === 'Paid' ? 'green' : 'red' }}>
                                                            {group.status === 'Paid' ? 'Yes' : 'No'}
                                                            {group.status !== 'Paid' && (
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    style={{ marginLeft: "20px" }}
                                                                    checked={selectedPayments.includes(group.stockPaymentId)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedPayments([...selectedPayments, group.stockPaymentId]);
                                                                        } else {
                                                                            setSelectedPayments(selectedPayments.filter(id => id !== group.stockPaymentId));
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {showPayments && cheques[group.stockPaymentId]?.length > 0 && (
                                                        <tr>
                                                            <td colSpan="8" style={{ padding: '12px' }}>
                                                                <div style={{ margin: '10px 0', padding: '10px', backgroundColor: '#f7f1fa', borderRadius: '15px' }}>
                                                                    <h5 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
                                                                        Cheque Details
                                                                    </h5>

                                                                    {cheques[group.stockPaymentId]?.length > 0 ? (
                                                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                            <thead>
                                                                                <tr style={{ backgroundColor: '#f0e6f5' }}>
                                                                                    <th style={{ padding: '12px', textAlign: 'center' }}>Cheque Number</th>
                                                                                    <th style={{ padding: '12px', textAlign: 'center' }}>Amount</th>
                                                                                    <th style={{ padding: '12px', textAlign: 'center' }}>Issue Date</th>
                                                                                    <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {cheques[group.stockPaymentId].map((cheque, chequeIndex) => (
                                                                                    <tr key={cheque.chequeId} style={{ backgroundColor: chequeIndex % 2 === 0 ? '#f7f1fa' : '#f0e6f5' }}>
                                                                                        <td style={{ padding: '12px', textAlign: 'center' }}>{cheque.chequeNumber}</td>
                                                                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                                                                            LKR {Number(cheque.chequeAmount).toLocaleString('en-US')}
                                                                                        </td>
                                                                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                                                                            {new Date(cheque.issuedDate).toLocaleDateString('en-GB')}
                                                                                        </td>
                                                                                        <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: cheque.chequeStatus === 'Pending' ? 'red' : 'green' }}>{cheque.chequeStatus}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    ) : (
                                                                        <div style={{ padding: '12px', textAlign: 'center' }}>
                                                                            No cheque information available
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>
                                                    No matching transactions found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {showPaymentModal && (
                                <StockVisePayment
                                    showModal={showPaymentModal}
                                    closeModal={() => setShowPaymentModal(false)}
                                    onSave={() => {
                                        fetchDueAmount(supplierId);
                                        fetchChequeAmount(supplierId);
                                        fetchDueAmountCount(supplierId);
                                        fetchStocks(supplierId);
                                        setSelectedPayments([]);
                                    }}
                                    selectedStockPayments={selectedStockPayments}
                                    supplierName={supplierName}
                                    supplierId={supplierId}
                                />
                            )}

                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', padding: '20px 0' }}>

                                    <button
                                        onClick={handlePrevious}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
                                            color: currentPage === 1 ? '#6c757d' : 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Previous
                                    </button>

                                    {displayPages.map(number => (
                                        <button
                                            key={number}
                                            onClick={() => setCurrentPage(number)}
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor: number === currentPage ? '#007bff' : '#f8f9fa',
                                                color: number === currentPage ? 'white' : '#495057',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: number === currentPage ? 'bold' : 'normal'
                                            }}
                                        >
                                            {number}
                                        </button>
                                    ))}

                                    <button
                                        onClick={handleNext}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
                                            color: currentPage === totalPages ? '#6c757d' : 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
            {error && <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: '1rem 0 0 0', textAlign: 'center' }}>{error}</p>}
        </div >
    )
}

export default SupplierAllDues